import type { EmailUnsubscribeState } from "@/services/apiTypes";
import {
  useResubscribeAllEmailMutation,
  useResubscribeEmailMutation,
  useUnsubscribeAllEmailMutation,
  useUnsubscribeEmailMutation,
} from "@/services/preferencesApi";
import { toast } from "@Light/components/page/toast";
import React, { useEffect, useRef, useState } from "react";
import { EmailUnsubscribeInvalidScreen } from "./EmailUnsubscribeInvalidScreen";
import { EmailUnsubscribeScreen } from "./EmailUnsubscribeScreen";

export const EmailUnsubscribe: React.FC = () => {
  const [unsubscribe, unsubscribeResult] = useUnsubscribeEmailMutation();
  const [resubscribe] = useResubscribeEmailMutation();
  const [unsubscribeAll] = useUnsubscribeAllEmailMutation();
  const [resubscribeAll] = useResubscribeAllEmailMutation();
  const [state, setState] = useState<EmailUnsubscribeState>();
  const [pending, setPending] = useState<"topic" | "all">();
  const triggeredRef = useRef(false);

  // Unsubscribe on open (avoid double submit)
  useEffect(() => {
    if (triggeredRef.current) {
      return;
    }
    triggeredRef.current = true;
    unsubscribe()
      .unwrap()
      .then(setState)
      .catch(() => undefined);
  }, [unsubscribe]);

  if (!state) {
    if (unsubscribeResult.isError) {
      return <EmailUnsubscribeInvalidScreen />;
    }
    return null;
  }

  const update = async (
    action: "topic" | "all",
    send: () => Promise<EmailUnsubscribeState>,
    successMessage: (next: EmailUnsubscribeState) => string,
  ) => {
    setPending(action);
    try {
      const next = await send();
      setState(next);
      toast.success(successMessage(next));
    } catch {
      toast.error(
        "Your subscription details couldn't be updated at this time. Please try again later.",
        {
          closeButton: true,
          duration: Infinity,
        },
      );
    } finally {
      setPending(undefined);
    }
  };

  const topicSubscribed = state.preferences[state.topic];
  const allSubscribed = state.preferences.all_marketing;

  return (
    <EmailUnsubscribeScreen
      topicName={state.topic_name}
      companyName={state.company_name}
      email={state.email}
      topicSubscribed={topicSubscribed}
      allSubscribed={allSubscribed}
      pending={pending}
      onToggleTopic={() =>
        update(
          "topic",
          () => (topicSubscribed ? unsubscribe() : resubscribe()).unwrap(),
          (next) =>
            topicSubscribed
              ? `You've unsubscribed from ${next.topic_name}`
              : `You've resubscribed to ${next.topic_name}`,
        )
      }
      onToggleAll={() =>
        update(
          "all",
          () => (allSubscribed ? unsubscribeAll() : resubscribeAll()).unwrap(),
          () =>
            allSubscribed
              ? "You've unsubscribed from all emails"
              : "You've resubscribed to all emails",
        )
      }
    />
  );
};
